import Swiper, { SwiperSlide } from '@/components/common/Swiper';
import useTranslation from 'next-translate/useTranslation';
import { FC, useEffect, useRef, useState } from 'react';
import PLACEHOLDER_LOGO from '@/assets/images/logo/weee-placeholder-logo.png';
import { twMerge } from 'tailwind-merge';
import { SubCompProps } from '../../component/LayoutLar';
import { getHomePageDataByUrl } from '@/api/content';
import { CroppedImage, getCroppedImageUrl } from '@weee-fe/module';
import { CmsComponentKeys } from '../..';
import ImpTracker from '@/components/common/ImpTracker';
import useImpressionT2 from '@/tracks/useImpressionT2';
import useClickActionT2 from '@/tracks/useClickActionT2';
import styles from './banner.module.scss';
import WeeeVideo from '@/components/pdp/Picture/video';

type SubBannerItem = {
  id: number;
  detail: string;
  img_url: string;
  img: string;
  title: string;
  pos: number;
  media_type: 'image' | 'video';
  media_url: string;
  url: string;
  cover_image_url?: string;
  autoplay: string;
};

const SubBanner: FC<SubCompProps> = props => {
  const {
    postUrl,
    onFetch,
    cmsComponentKey,
    componentPos,
    properties,
    componentDataSourceKey,
    subComponentPos,
    layoutKey
  } = props;
  const { lang } = useTranslation('common');
  const [load, setLoad] = useState(true);
  const eventKey = properties?.event_key || cmsComponentKey;
  const [data, setData] = useState<SubBannerItem[]>([]);
  const [autoplay, setAutoplay] = useState<boolean>(false);

  const fetchData = async () => {
    try {
      const { result, object } = await getHomePageDataByUrl(postUrl, {
        lang,
        dataobject_key: componentDataSourceKey
      });
      if (result && Array.isArray(object?.carousel)) {
        if (cmsComponentKey === CmsComponentKeys.CM_SUB_BANNER && object?.carousel.length === 0)
          return onFetch('error');
        if (cmsComponentKey === CmsComponentKeys.CM_SUB_BANNER_LIST && object?.carousel.length < 3)
          return onFetch('error');
        setLoad(false);
        setData(object.carousel);
        setAutoplay(!!object?.autoplay);
        onFetch('done');
      } else {
        onFetch('error');
      }
    } catch (error) {
      onFetch('error');
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  if (load) {
    return <SubBannerSkeleton />;
  }

  return (
    <div className="rounded-300 h-[283px]">
      {data ? (
        <>
          {cmsComponentKey === CmsComponentKeys.CM_SUB_BANNER && (
            <>
              {data.length === 1 && (
                <SubBannerItem
                  height={283}
                  item={data[0]}
                  index={0}
                  eventKey={eventKey}
                  componentPos={componentPos}
                  subComponentPos={subComponentPos}
                  layoutKey={layoutKey}
                />
              )}
              {data.length > 1 && (
                <div className={`w-full h-full`}>
                  <Swiper
                    spaceBetween={20}
                    pagination={{
                      dynamicBullets: true,
                      dynamicMainBullets: 3
                    }}
                    loop
                    autoplay={
                      autoplay
                        ? {
                            delay: 10000,
                            disableOnInteraction: false
                          }
                        : false
                    }
                    slidesPerView={1}
                    className={twMerge('rounded-400 h-full', styles.swiper)}>
                    {data.map((item, index) => {
                      return (
                        <SwiperSlide key={`banner-${index}`}>
                          <SubBannerItem
                            height={283}
                            item={item}
                            index={index}
                            eventKey={eventKey}
                            componentPos={componentPos}
                            subComponentPos={subComponentPos}
                            layoutKey={layoutKey}
                          />
                        </SwiperSlide>
                      );
                    })}
                  </Swiper>
                </div>
              )}
            </>
          )}
          {cmsComponentKey === CmsComponentKeys.CM_SUB_BANNER_LIST && (
            <div className={`grid h-full gap-3.5 grid-rows-3`}>
              {data.slice(0, 3).map((item, index) => {
                return (
                  <SubBannerItem
                    key={item.id}
                    height={283 / 3}
                    item={item}
                    index={index}
                    eventKey={eventKey}
                    componentPos={componentPos}
                    subComponentPos={subComponentPos}
                    layoutKey={layoutKey}
                  />
                );
              })}
            </div>
          )}
        </>
      ) : (
        <SubBannerSkeleton />
      )}
    </div>
  );
};

export default SubBanner;

const SubBannerItem: FC<{
  height: number;
  item: SubBannerItem;
  index: number;
  eventKey: string;
  componentPos: number;
  subComponentPos: number;
  layoutKey: string;
}> = ({ height, item, index, eventKey, componentPos, subComponentPos, layoutKey }) => {
  const { bannerImpression } = useImpressionT2();
  const { bannerClick } = useClickActionT2();

  const bannerTrackParams = {
    mod_nm: layoutKey,
    mod_pos: componentPos,
    sec_nm: eventKey,
    sec_pos: subComponentPos,
    co: {
      target_nm: item.id ?? 0,
      banner_pos: index,
      url: item?.detail,
      banner_type: item.media_type,
      banner_id: item.id
    },
    ctx: {}
  };

  return (
    <a href={item.detail} key={item.id} className="w-full h-full">
      <ImpTracker
        className="w-full h-full"
        impTrackingT2={() => {
          bannerImpression(bannerTrackParams);
        }}
        clickTrackingT2={() => {
          bannerClick(bannerTrackParams);
        }}>
        {item.media_type === 'video' ? (
          <WeeeVideo
            autoPlay={item.autoplay === '1'}
            animation={false}
            poster={getCroppedImageUrl(item.img_url, { renderHeight: height, devicePixelRatio: 2 })}
            posterClassName="w-full h-full object-cover"
            muteClassName="w-8 h-8 right-1 bottom-1"
            videoClassName="w-full h-full absolute rounded object-cover"
            controlPanel={{ showPlayIcon: true, showMuteIcon: true }}
            defaultProps={{ showPlayIcon: true, showMuteIcon: false }}
            className={'h-full'}
            src={item.media_url}
            onPlayIconClick={e => e.preventDefault()}
            muteIconUrl={require('@/components/pdp/Picture/video/images/home-muted.svg')}
            soundIconUrl={require('@/components/pdp/Picture/video/images/home-sound.svg')}
          />
        ) : (
          <CroppedImage
            url={item.img_url || item.media_url}
            configs={{ renderHeight: height }}
            className="w-full h-full rounded-300 border border-solid border-[#0000000A]"
            placeholder={<SubBannerSkeleton />}
            alt={`weee sub banner image ${index}`}
          />
        )}
      </ImpTracker>
    </a>
  );
};

export const SubBannerSkeleton: FC = () => {
  return (
    <div
      className="w-full h-full"
      style={{
        backgroundRepeat: 'no-repeat',
        backgroundPosition: 'center',
        backgroundImage: `url(${PLACEHOLDER_LOGO})`,
        backgroundSize: '105px 31.5px'
      }}
    />
  );
};
